// Skills can carry the same 'attempted' state as problems: it's a derived
// value (computed in lib/derivation.ts from getSolvedSet + isAttempted), not
// asserted statically in REALMS. Adding it here keeps RealmPage's
// `Record<SkillState, ...>` lookup-tables exhaustive.
export type SkillState = 'done' | 'current' | 'todo' | 'locked' | 'attempted';

export interface Skill {
  id: string;
  name: string;
  pun: string;
  state: SkillState;
  n: number;
  gate?: string;
}

export interface Realm {
  id: string;
  name: string;
  pun: string;
  emoji: string;
  color: string;
  x: number;
  y: number;
  w: number;
  h: number;
  skills: Skill[];
}

export interface Ferry { from: string; to: string; }

export type DiffLevel = 'easy' | 'med' | 'hard';
// 'attempted' = user opened the problem and edited the starter but hasn't passed
// all tests. Derived at render time from sb.code.v1.* localStorage; see
// `isAttempted` in lib/progress.ts.
export type ProblemState = 'done' | 'current' | 'todo' | 'locked' | 'attempted';

export interface SkillProblem {
  n: number;
  name: string;
  diff: DiffLevel;
  state: ProblemState;
  time: string;
  tags: string;
  gate?: string;
  problemId?: string;
}

export interface Trophy {
  e: string;
  name: string;
  desc: string;
  earned: boolean;
  fam: string;
}

// ── Realms ────────────────────────────────────────────────

// NOTE: `state` here is intentionally `'todo'` or `'locked'` only — never
// 'done' or 'current'. Real progress is derived at render time in
// `lib/derivation.ts` from `getSolvedSet()`. 'locked' means XP-gated and is
// the only state the static data is allowed to assert.
//
// Layout: realms are arranged in learning order across two rows so the path a
// student walks (syntax → algo → oop → sys → api → react → ai) reads
// left-to-right, top row, then left-to-right, bottom row. Coordinates target
// the fixed CANVAS_W × CANVAS_H box used in IslandMap; that container is then
// auto-scaled to fit the viewport. Keep new realms inside that box (1180 wide,
// 620 tall) so the scaler never has to clip.
export const REALMS: Realm[] = [
  {
    id: 'lang', name: 'Syntax Shoals', pun: 'where one-liners come ashore', emoji: '🪸',
    color: 'tan', x: 30, y: 60, w: 240, h: 230,
    skills: [
      { id: 'async',   name: 'Async Arête',     pun: 'await your turn',       state: 'todo',   n: 5 },
      { id: 'thr',     name: 'Thread Tundra',   pun: "GIL got your tongue",   state: 'locked', n: 0, gate: 'lv 6' },
      { id: 'mem',     name: 'Memory Mesa',     pun: 'GC and chill',          state: 'locked', n: 0, gate: 'lv 7' },
      { id: 'gen',     name: 'Generator Glade', pun: 'yield to no one',       state: 'todo',   n: 5 },
      { id: 'ts',      name: 'Type Tundra',     pun: 'narrow it down',        state: 'todo',   n: 0 },
      { id: 'closure', name: 'Closure Cliff',   pun: 'remembers everything',  state: 'todo',   n: 5 },
      { id: 'pysyn',   name: 'Pythonic Pier',   pun: 'one-liners welcome',    state: 'todo',   n: 5 },
      { id: 'jssyn',   name: 'JS Quirk Quay',   pun: 'spread joy, not arrays',state: 'todo',   n: 5 },
    ],
  },
  {
    id: 'algo', name: 'Algo Isles', pun: 'where Big-O grows on trees', emoji: '🏝',
    color: 'amber', x: 290, y: 60, w: 290, h: 230,
    skills: [
      { id: 'arr',   name: 'Array Cove',          pun: 'index 0 means business',       state: 'todo',   n: 6 },
      { id: 'str',   name: 'String Strand',        pun: 'palindromes only',             state: 'todo',   n: 6 },
      { id: 'hash',  name: 'Hash Harbor',           pun: 'where keys dock',              state: 'todo',   n: 8 },
      { id: 'two',   name: 'Two-Pointer Pier',      pun: 'left meets right',             state: 'todo',   n: 7 },
      { id: 'slid',  name: 'Sliding Window Bay',    pun: 'shift happens',                state: 'todo',   n: 5 },
      { id: 'stk',   name: 'Stack Stack Stack',     pun: 'LIFO of the party',            state: 'todo',   n: 6 },
      { id: 'q',     name: 'Queue Quay',            pun: 'first in, first served',       state: 'todo',   n: 5 },
      { id: 'll',    name: 'Linked List Lagoon',    pun: '.next adventure',              state: 'todo',   n: 5 },
      { id: 'tree',  name: 'Tree Atoll',            pun: 'leaves you wanting more',      state: 'todo',   n: 6 },
      { id: 'graph', name: 'Graph Grotto',          pun: 'mind your edges',              state: 'locked', n: 6, gate: 'lv 8' },
      { id: 'dp',    name: 'DP Dunes',              pun: 'subproblem city',              state: 'todo',   n: 7 },
      { id: 'bt',    name: 'Backtrack Beach',       pun: 'undo, redo, repeat',           state: 'todo',   n: 6 },
      { id: 'gr',    name: 'Greedy Gully',          pun: 'take the best, skip the rest', state: 'locked', n: 5, gate: 'lv 6' },
      { id: 'bs',    name: 'Binary Search Sea',     pun: 'half the time',                state: 'todo',   n: 5 },
      { id: 'srt',   name: 'Sort Shores',           pun: 'merge, quick, or bust',        state: 'todo',   n: 5 },
    ],
  },
  {
    id: 'oop', name: 'OOP Town', pun: 'population: instances', emoji: '🏛',
    color: 'peach', x: 600, y: 60, w: 270, h: 230,
    skills: [
      { id: 'cls',    name: 'Class Plaza',            pun: 'where blueprints live',          state: 'todo',   n: 2 },
      { id: 'inh',    name: 'Inheritance Heights',    pun: 'extends only',                   state: 'todo',   n: 3 },
      { id: 'enc',    name: 'Encapsulation Ave',      pun: 'private property',               state: 'todo',   n: 2 },
      { id: 'poly',   name: 'Polymorphism Park',      pun: 'many shapes, one call',          state: 'todo',   n: 2 },
      { id: 'solid',  name: 'SOLID Square',           pun: 'five-letter wisdom',             state: 'todo',   n: 2 },
      { id: 'design', name: 'Design Plaza',           pun: 'LRU, MinStack, and friends',     state: 'todo',   n: 7 },
      { id: 'pat',    name: 'Pattern District',       pun: 'Singletons drink alone',         state: 'locked', n: 2, gate: 'lv 5' },
      { id: 'iface',  name: 'Interface Intersection', pun: 'shake on the contract',          state: 'todo',   n: 3 },
      { id: 'abs',    name: 'Abstract Alley',         pun: 'leave details to children',      state: 'todo',   n: 3 },
    ],
  },
  {
    id: 'sys', name: 'System City', pun: 'scales of justice... and load', emoji: '🏗',
    color: 'coral', x: 890, y: 60, w: 270, h: 230,
    skills: [
      { id: 'lb',    name: 'Load Balancer Blvd',  pun: 'spread the love',          state: 'todo',   n: 2 },
      { id: 'cache', name: 'Cache Causeway',       pun: 'TTL the truth',            state: 'todo',   n: 2 },
      { id: 'db',    name: 'Database District',    pun: 'normalize, then forget',   state: 'locked', n: 0, gate: 'lv 7' },
      { id: 'shrd',  name: 'Sharding Suburbs',     pun: 'split the difference',     state: 'locked', n: 2, gate: 'lv 9' },
      { id: 'q1',    name: 'Message Queue Quarter',pun: 'fire and forget',          state: 'locked', n: 2, gate: 'lv 8' },
      { id: 'cdn',   name: 'CDN Crossroads',       pun: 'edge of glory',            state: 'locked', n: 0, gate: 'lv 8' },
      { id: 'rl',    name: 'Rate Limit Row',        pun: 'slow your roll',           state: 'todo',   n: 2 },
      { id: 'cap',   name: 'CAP Theorem Tower',    pun: 'pick two',                 state: 'locked', n: 0, gate: 'lv 10' },
    ],
  },
  {
    id: 'api', name: 'API Avenue', pun: 'where requests retire', emoji: '🛣',
    color: 'tan', x: 40, y: 330, w: 255, h: 230,
    skills: [
      { id: 'rest', name: 'REST Stop',           pun: 'verbs of intent',         state: 'todo',   n: 10 },
      { id: 'gql',  name: 'GraphQL Grove',       pun: 'ask for what you want',   state: 'todo',   n: 5 },
      { id: 'grpc', name: 'gRPC Gateway',        pun: 'protobuf or perish',      state: 'locked', n: 3, gate: 'lv 7' },
      { id: 'auth', name: 'Auth Annex',           pun: "who goes there?",         state: 'todo',   n: 5 },
      { id: 'ver',  name: 'Versioning Village',  pun: 'v1 forever',              state: 'todo',   n: 5 },
      { id: 'wbh',  name: 'Webhook Wynd',        pun: 'callbacks call back',     state: 'locked', n: 5, gate: 'lv 6' },
      { id: 'id',   name: 'Idempotent Inn',       pun: 'try again, same result',  state: 'todo',   n: 5 },
    ],
  },
  {
    id: 'react', name: 'React Realm', pun: 'state of the union', emoji: '⚛',
    color: 'cream', x: 590, y: 330, w: 265, h: 230,
    skills: [
      { id: 'jsx',   name: 'JSX Junction',           pun: 'HTML in disguise',      state: 'todo',   n: 1 },
      { id: 'props', name: 'Props Pier',              pun: 'pass it down',          state: 'todo',   n: 1 },
      { id: 'state', name: 'State Station',           pun: 'useState abuse',        state: 'todo',   n: 1 },
      { id: 'eff',   name: 'Effect Embankment',       pun: 'side effects only',     state: 'todo',   n: 1 },
      { id: 'ctx',   name: 'Context Common',          pun: 'prop drilling cure',    state: 'todo',   n: 1 },
      { id: 'hk',    name: 'Custom Hook Hollow',      pun: 'use-everything',        state: 'todo',   n: 1 },
      { id: 'perf',  name: 'Performance Peninsula',   pun: 'memo or weep',          state: 'todo',   n: 1, gate: 'lv 7' },
      { id: 'ssr',   name: 'SSR Strait',              pun: 'hydrate responsibly',   state: 'locked', n: 0, gate: 'lv 9' },
      { id: 'rsc',   name: 'Server Component Shore',  pun: 'async and you',         state: 'locked', n: 0, gate: 'lv 10' },
    ],
  },
  {
    id: 'ai', name: 'AI Atoll', pun: 'attention is all you need (and coffee)', emoji: '🤖',
    color: 'amber', x: 875, y: 330, w: 265, h: 230,
    skills: [
      { id: 'tok',   name: 'Tokenizer Trail',     pun: 'subword savings',               state: 'todo',   n: 5 },
      { id: 'embed', name: 'Embedding Estuary',   pun: 'cosine close friends',          state: 'todo',   n: 5 },
      { id: 'attn',  name: 'Attention Arch',       pun: 'softmax of dreams',             state: 'todo',   n: 5 },
      { id: 'fa',    name: 'FlashAttention Falls', pun: 'tiles for breakfast',           state: 'locked', n: 5, gate: 'lv 8' },
      { id: 'inf',   name: 'Inference Inlet',      pun: 'prefill, then decode',          state: 'todo',   n: 5 },
      { id: 'kv',    name: 'KV Cache Cay',         pun: 'remember everything',           state: 'locked', n: 5, gate: 'lv 7' },
      { id: 'ft',    name: 'Fine-tune Foothills',  pun: 'lower your rank',               state: 'locked', n: 5, gate: 'lv 8' },
      { id: 'rag',   name: 'RAG Reef',             pun: 'retrieve before you generate',  state: 'todo',   n: 5 },
      { id: 'evals', name: 'Evals Lighthouse',     pun: 'measure twice',                 state: 'todo',   n: 5 },
      { id: 'tool',  name: 'Tool-Call Tideline',   pun: 'name your arguments',           state: 'todo',   n: 0 },
      { id: 'mcp',   name: 'MCP Marina',           pun: 'tools, listed and called',      state: 'todo',   n: 0 },
      { id: 'agent', name: 'Agent Archipelago',    pun: "loop till you're done",         state: 'todo',   n: 0 },
    ],
  },
  {
    // Full-stack app development: multi-file backends (Python/Node) graded by
    // synthetic HTTP requests, all in-browser. Sits between API Avenue (where
    // you learn request/response) and React Realm (the frontend), since a
    // full-stack app is where the two meet.
    id: 'fs', name: 'Full-Stack Bay', pun: 'where front meets back', emoji: '🌐',
    color: 'peach', x: 315, y: 330, w: 255, h: 230,
    skills: [
      { id: 'fsauth', name: 'Auth Harbor',    pun: 'login, then linger',     state: 'todo',   n: 3 },
      { id: 'fsrest', name: 'Service Wharf',   pun: 'CRUD and carry on',      state: 'todo',   n: 3 },
      { id: 'fsui',   name: 'Frontend Quay',   pun: 'fetch, then render',     state: 'todo',   n: 0 },
    ],
  },
];

export const FERRIES: Ferry[] = [
  // Primary progression along the U-shape (top L→R, then bottom L→R).
  { from: 'lang', to: 'algo'  },
  { from: 'algo', to: 'oop'   },
  { from: 'oop',  to: 'sys'   },
  { from: 'sys',  to: 'api'   },
  { from: 'api',  to: 'fs'    },
  { from: 'fs',   to: 'react' },
  { from: 'react', to: 'ai'   },
  // Diagonal shortcut for learners who already know syntax + algos and want
  // to dive straight into systems thinking.
  { from: 'algo', to: 'api'   },
];

// ── Trophies ──────────────────────────────────────────────

export const TROPHIES: Trophy[] = [
  { e: '🔥', name: 'first spark',      desc: '1-day streak',          earned: true,  fam: 'streak' },
  { e: '🔥', name: 'week warrior',     desc: '7-day streak',          earned: true,  fam: 'streak' },
  { e: '🔥', name: 'fortnight phenom', desc: '14-day streak',         earned: false, fam: 'streak' },
  { e: '🔥', name: 'monthly menace',   desc: '30-day streak',         earned: false, fam: 'streak' },
  { e: '🔥', name: 'centurion',        desc: '100-day streak',        earned: false, fam: 'streak' },
  { e: '🔥', name: 'year of code',     desc: '365-day streak',        earned: false, fam: 'streak' },

  { e: '🐣', name: 'first solve',      desc: 'solve your first problem', earned: true,  fam: 'count' },
  { e: '🥇', name: 'first 10',         desc: 'solve 10 problems',        earned: true,  fam: 'count' },
  { e: '🎯', name: 'half-century',     desc: '50 problems',              earned: true,  fam: 'count' },
  { e: '💯', name: 'centurion solver', desc: '100 problems',             earned: false, fam: 'count' },
  { e: '🏅', name: 'half a thousand',  desc: '500 problems',             earned: false, fam: 'count' },
  { e: '👑', name: 'thousand-club',    desc: '1000 problems',            earned: false, fam: 'count' },

  { e: '🏝', name: 'island hopper',    desc: 'visit 3 realms',        earned: true,  fam: 'realm' },
  { e: '🗺', name: 'cartographer',     desc: 'visit all 8 realms',    earned: false, fam: 'realm' },
  { e: '🏝', name: 'algo native',      desc: 'finish Algo Isles',     earned: false, fam: 'realm' },
  { e: '🏛', name: 'OOP mayor',        desc: 'finish OOP Town',       earned: false, fam: 'realm' },
  { e: '🏗', name: 'system architect', desc: 'finish System City',    earned: false, fam: 'realm' },
  { e: '🛣', name: 'API ambassador',   desc: 'finish API Avenue',     earned: false, fam: 'realm' },
  { e: '🌐', name: 'full-stack founder', desc: 'finish Full-Stack Bay', earned: false, fam: 'realm' },
  { e: '⚛', name: 'state of mind',    desc: 'finish React Realm',    earned: false, fam: 'realm' },
  { e: '🤖', name: 'AI wanderer',      desc: 'finish AI Atoll',       earned: false, fam: 'realm' },
  { e: '🪸', name: 'syntax sage',      desc: 'finish Syntax Shoals',  earned: false, fam: 'realm' },

  { e: '🐍', name: 'pythonista', desc: 'solve 25 in Python',  earned: true,  fam: 'lang' },
  { e: '🟨', name: 'JS ninja',   desc: 'solve 25 in JS/TS',   earned: false, fam: 'lang' },
  { e: '🌐', name: 'bilingual',  desc: 'solve in both langs', earned: true,  fam: 'lang' },

  { e: '🌳', name: 'tree whisperer', desc: 'master all tree problems',     earned: true,  fam: 'skill' },
  { e: '📈', name: 'DP devotee',     desc: 'finish DP Dunes',              earned: false, fam: 'skill' },
  { e: '🔗', name: 'chain reaction', desc: 'finish Linked List Lagoon',    earned: false, fam: 'skill' },
  { e: '🕸',  name: 'graph guru',    desc: 'finish Graph Grotto',          earned: false, fam: 'skill' },

  { e: '🚀', name: 'speedrunner',  desc: 'solve a med in under 10m',   earned: true,  fam: 'speed' },
  { e: '⚡', name: 'lightning rod', desc: 'solve a hard in under 20m', earned: false, fam: 'speed' },
  { e: '🏃', name: 'no hints',     desc: 'solve 10 without hints',     earned: true,  fam: 'speed' },
  { e: '🎯', name: 'one-shot',     desc: 'pass on first submit',       earned: true,  fam: 'speed' },

  { e: '👥', name: 'social butterfly', desc: 'add 5 friends',     earned: false, fam: 'social' },
  { e: '🥇', name: 'top 10 daily',    desc: 'crack daily top 10', earned: false, fam: 'social' },
  { e: '🏆', name: 'top 1 daily',     desc: 'win the daily',      earned: false, fam: 'social' },

  { e: '🌙', name: 'night owl',   desc: 'solve after midnight', earned: false, fam: 'time' },
  { e: '🌅', name: 'early bird',  desc: 'solve before 6am',     earned: true,  fam: 'time' },
  { e: '☕', name: 'lunch break', desc: 'solve during noon',    earned: true,  fam: 'time' },

  { e: '🐝', name: 'fizzbuzz friend', desc: 'meet the mascot',             earned: true,  fam: 'rare' },
  { e: '💎', name: 'perfectionist',   desc: '100% accuracy on a realm',    earned: false, fam: 'rare' },
  { e: '🎓', name: 'graduate',        desc: 'finish all 8 realms',         earned: false, fam: 'rare' },
  { e: '🦄', name: 'unicorn',         desc: 'find a hidden trophy',        earned: false, fam: 'rare' },
  { e: '🎪', name: 'jack of all',     desc: 'try every realm',             earned: true,  fam: 'rare' },
  { e: '🐌', name: 'slow & steady',   desc: 'solve 1/day for 30 days',     earned: false, fam: 'rare' },
];

export const FAM_LABELS: Record<string, string> = {
  streak: 'Streak',
  count:  'Volume',
  realm:  'Realms',
  lang:   'Languages',
  skill:  'Skills',
  speed:  'Speed & Precision',
  social: 'Social',
  time:   'Time of Day',
  rare:   'Rare',
};
